import { type HTMLElement, parse, type TextNode } from 'node-html-parser';

import { LUMINA_TAGS } from './LUMINA_TAGS';
import type { Chunk } from './types';

/**
 * Splits a mixed markdown / Lumina XML string into ordered chunks, so prose can
 * later go through the markdown transformer (keeping bold/italic/etc. marks) and
 * Lumina XML through `luminaToAdf` (keeping mentions, smart links, etc.).
 *
 * Parsing is delegated to `node-html-parser` rather than a regex to handle
 * nested tags, attribute quoting, and whitespace correctly. The transport-only
 * `<lumina>` wrapper is unwrapped first (see below) so wrapped and bare payloads
 * are treated identically.
 */
export const segmentLuminaAndProse = (mixed: string): Chunk[] => {
	const trimmed = mixed.trim();
	if (trimmed.length === 0) {
		return [];
	}

	const root = parse(trimmed);

	// `<lumina>` is the outer wrapper the chat stream uses to delimit a payload.
	// The server only ever sends a single, top-level wrapper, so a one-level
	// unwrap is sufficient (no recursion needed).
	const flatNodes: typeof root.childNodes = [];
	for (const node of root.childNodes) {
		const el = node as HTMLElement;
		if (el.tagName?.toLowerCase() === 'lumina') {
			for (const child of el.childNodes) {
				flatNodes.push(child);
			}
		} else {
			flatNodes.push(node);
		}
	}

	const chunks: Chunk[] = [];

	for (const node of flatNodes) {
		const el = node as HTMLElement;
		const tagName = el.tagName?.toLowerCase();
		const text = (tagName ? el.outerHTML : (node as TextNode).text)?.trim();
		if (!text) {
			continue;
		}

		const isLumina = tagName && LUMINA_TAGS.has(tagName);
		const luminaKind = tagName === 'section' ? 'lumina-document' : 'lumina-fragment';
		chunks.push({
			kind: isLumina ? luminaKind : 'prose',
			text,
		});
	}

	return chunks;
};
